import { useRef, useState } from "react";
import { FileUp, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DraftSourceDocumentChip } from "@/components/draft/DraftSourceDocumentChip";
import {
  DRAFT_SOURCE_DOC_LABELS,
  type DraftSourceDocType,
  type DraftSourceDocumentInput,
} from "@/types/drafting";
import { EmailPicker, type EmailImportPayload } from "@/components/wizard/EmailPicker";

const DOC_TYPES: DraftSourceDocType[] = [
  "guideline",
  "playbook",
  "precedent",
  "background",
  "party-source",
];

interface SourceDocumentsUploaderProps {
  documents: DraftSourceDocumentInput[];
  onChange: (documents: DraftSourceDocumentInput[]) => void;
  maxDocuments?: number;
}

export function SourceDocumentsUploader({
  documents,
  onChange,
  maxDocuments = 10,
}: SourceDocumentsUploaderProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const activeTypeRef = useRef<DraftSourceDocType>("guideline");
  const [emailPickerOpen, setEmailPickerOpen] = useState(false);

  function handlePick(docType: DraftSourceDocType) {
    activeTypeRef.current = docType;
    inputRef.current?.click();
  }

  function handleFiles(files: FileList | null) {
    if (!files?.length) return;
    const remaining = maxDocuments - documents.length;
    const selected = Array.from(files).slice(0, remaining);
    const next = [
      ...documents,
      ...selected.map((file) => ({
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        fileName: file.name,
        docType: activeTypeRef.current,
      })),
    ];
    onChange(next);
  }

  function removeDoc(id: string) {
    onChange(documents.filter((doc) => doc.id !== id));
  }

  function handleEmailImport(payload: EmailImportPayload) {
    const threadFile = new File(
      [payload.threadText],
      `${payload.conversation.subject || "email-thread"}.txt`,
      { type: "text/plain" },
    );
    const imported = [
      {
        id: `email-thread-${Date.now()}`,
        file: threadFile,
        fileName: threadFile.name,
        docType: "background" as const,
      },
      ...payload.files.map(({ file }, index) => ({
        id: `email-attachment-${Date.now()}-${index}`,
        file,
        fileName: file.name,
        docType: "background" as const,
      })),
    ].slice(0, maxDocuments - documents.length);
    onChange([...documents, ...imported]);
    setEmailPickerOpen(false);
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="mb-2 text-sm font-medium text-[var(--lm-foreground-strong)] dark:text-slate-200">
          Source documents
        </p>
        <p className="mb-3 text-xs text-[var(--lm-muted)] dark:text-slate-400">
          Attach reference materials. Choose a document type before uploading.
        </p>
        <div className="flex flex-wrap gap-2">
          {DOC_TYPES.map((docType) => (
            <Button
              key={docType}
              type="button"
              variant="outline"
              size="sm"
              disabled={documents.length >= maxDocuments}
              onClick={() => handlePick(docType)}
              className="gap-2"
            >
              <FileUp className="h-3.5 w-3.5" />
              <span className="text-[10px] font-semibold uppercase tracking-wide">
                {DRAFT_SOURCE_DOC_LABELS[docType]}
              </span>
            </Button>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={documents.length >= maxDocuments}
            onClick={() => setEmailPickerOpen(true)}
            className="gap-2"
          >
            <Mail className="h-3.5 w-3.5" />
            <span className="text-[10px] font-semibold uppercase tracking-wide">Emails</span>
          </Button>
        </div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".pdf,.docx,.txt"
          multiple
          onChange={(e) => {
            handleFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {documents.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {documents.map((doc) => (
            <DraftSourceDocumentChip
              key={doc.id}
              docType={doc.docType}
              fileName={doc.file?.name ?? doc.fileName}
              onRemove={() => removeDoc(doc.id)}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-[var(--lm-muted)] dark:text-slate-500">
          No source documents attached yet.
        </p>
      )}
      <EmailPicker
        open={emailPickerOpen}
        onOpenChange={setEmailPickerOpen}
        destination="draft"
        onImport={handleEmailImport}
      />
    </div>
  );
}
